import type { Messages, PluralKey } from "./en";

/** Arabic plural forms beyond English's `.one` and `.other`. */
type ArabicPluralForms = Partial<Record<`${PluralKey}.${"zero" | "two" | "few" | "many"}`, string>>;

/** Arabic interface messages: every English key, in the wording S uses for Foodics BI. */
export const ar = {
  "common.cancel": "إلغاء",
  "common.submit": "إرسال",
  "common.edit": "تعديل",
  "common.close": "إغلاق",
  "ui.sidebar": "الشريط الجانبي",
  "ui.sidebarDescription": "يعرض الشريط الجانبي على الهاتف.",
  "ui.collapseSidebar": "طي الشريط الجانبي",
  "ui.expandSidebar": "توسيع الشريط الجانبي",
  "ui.toggleSidebar": "إظهار الشريط الجانبي أو إخفاؤه",
  "users.inviteSent": "أُرسلت تعليمات تسجيل الدخول بالبريد الإلكتروني",
  "common.done": "تم",
  "common.retry": "إعادة المحاولة",
  "common.search": "بحث",
  "common.loading": "جارٍ التحميل",
  "common.loadingEllipsis": "جارٍ التحميل...",
  "common.pleaseWait": "يُرجى الانتظار...",
  "common.business": "النشاط التجاري",
  "common.selectBusiness": "اختر النشاط التجاري",
  "common.name": "الاسم",
  "common.email": "البريد الإلكتروني",
  "common.users": "المستخدمون",
  "common.monthly": "شهري",
  "common.weekly": "أسبوعي",

  "signOut.title": "تسجيل الخروج",
  "common.dismiss": "إغلاق",
  "guard.businesses": "الأنشطة التجارية:",
  "guard.licenseTitle": "يلزم ترخيص أو إضافة BI",
  "guard.licenseBody":
    'لا يملك نشاطك التجاري "الإضافة/الترخيص" اللازم لمواصلة استخدام Foodics BI. يُرجى التواصل مع مدير حسابك لتمديد الفترة التجريبية المجانية أو تفعيل الإضافة.',
  "guard.error": "حدث خطأ. جرّب تحديث الصفحة أو تواصل مع الدعم.",
  "install.needsHelpTitle": "التثبيت يحتاج إلى مساعدة",
  "install.needsHelpBody": "تعذّر علينا إكمال تجهيز هذا النشاط التجاري. يُرجى التواصل مع الدعم.",
  "install.checkFailedTitle": "تعذّر التحقق من حالة التثبيت",
  "install.checkFailedBody": "سنعيد التحقق من الحالة تلقائيًا.",
  "install.inProgressTitle": "التثبيت قيد التنفيذ",
  "install.fetching": "يجري جلب بياناتك",
  "install.maxWait": "أقصى مدة انتظار: 30 دقيقة",
  "install.preparing": "يجري تجهيز بياناتك",
  "install.lotOfData": "لديك الكثير من البيانات 🙂",
  "embed.loadFailed": "تعذّر تحميل S. يُرجى تحديث هذه الصفحة أو التواصل مع الدعم.",
  "embed.dataContextChanged": "تغيّر نطاق بياناتك. حدّث هذه الصفحة للمتابعة.",
  "embed.businessUnavailable":
    "هذا النشاط التجاري غير متاح بعد. تحقّق من تثبيته وترخيصه في الإعدادات.",
  "embed.businessSwitchFailed": "تعذّر تغيير النشاط التجاري",
  "signOut.confirm": "هل أنت متأكد من أنك تريد تسجيل الخروج؟",

  "settings.title": "الإعدادات",
  "settings.backToHome": "العودة إلى الرئيسية",
  "settings.pages": "صفحات الإعدادات",
  "settings.page": "صفحة الإعدادات",
  "settings.organization": "المؤسسة",
  "settings.users": "المستخدمون",
  "settings.businesses": "الأنشطة التجارية",
  "settings.budgets": "الميزانيات (المستهدفات)",
  "settings.adminsOnly": "يمكن للمسؤولين فقط فتح الإعدادات.",

  "users.activeDescription": "الأشخاص الذين لديهم حسابات في BI",
  "users.inactiveDescription": "حسابات Foodics التي يمكنها تلقي تعليمات تسجيل الدخول",
  "users.accountType": "نوع الحساب",
  "users.biAccounts": "حسابات BI",
  "users.foodicsAccounts": "حسابات Foodics",
  "users.biAccount": "حساب BI",
  "users.foodicsAccount": "حساب Foodics",
  "users.pending": "قيد الانتظار",
  "users.unlicensed": "غير مرخّص",
  "users.biRole": "الدور في BI",
  "users.foodicsRoles": "الأدوار في Foodics",
  "users.branches": "الفروع",
  "users.noRoles": "لا يوجد",
  "users.owner": "المالك",
  "users.admin": "مسؤول",
  "users.normal": "عادي",
  "users.normalUser": "مستخدم عادي",
  "users.chooseSingleBusiness": "يُرجى اختيار نشاط تجاري واحد من القائمة.",
  "users.noFoodicsAccounts": "لم يتم العثور على حسابات Foodics",
  "users.noBIAccounts": "لم يتم العثور على حسابات BI",
  "users.status": "الحالة",
  "users.role": "الدور",
  "users.roles": "الأدوار",
  "users.invite": "دعوة",
  "users.branchCount.zero": "لا توجد فروع",
  "users.branchCount.one": "فرع واحد",
  "users.branchCount.two": "فرعان",
  "users.branchCount.few": "{count} فروع",
  "users.branchCount.many": "{count} فرعًا",
  "users.branchCount.other": "{count} فرع",
  "users.inviteFailed": "تعذّر إرسال الدعوة",
  "users.roleChanged": "تم تغيير دور المستخدم بنجاح.",
  "users.roleChangeNotPermitted": "تغيير الدور غير مسموح به",
  "users.roleChangeFailed": "تعذّر تحديث صلاحيات المستخدم",

  "sync.checking": "جارٍ التحقق من التحديث…",
  "sync.unavailable": "حالة التحديث غير متاحة",
  "sync.refreshingQueued": "جارٍ تحديث الحسابات، وطلب التحديث الخاص بك في قائمة الانتظار",
  "sync.refreshing": "جارٍ تحديث الحسابات…",
  "sync.yourRefreshQueued": "طلب التحديث الخاص بك في قائمة الانتظار",
  "sync.automaticRetry": "إعادة المحاولة تلقائيًا في {time}",
  "sync.queued": "التحديث في قائمة الانتظار",
  "sync.failedAt": "فشل · {time}",
  "sync.lastFailed": "فشل آخر تحديث",
  "sync.lastRun": "آخر تشغيل {time}",
  "sync.lastRunTitle": "آخر تشغيل: {time}",
  "sync.available": "التحديث متاح",
  "sync.anotherStarted": "بدأ تحديث آخر أولًا. لم تتم إضافة تحديث جديد إلى قائمة الانتظار.",
  "sync.alreadyQueued": "يوجد تحديث في قائمة الانتظار بالفعل.",
  "sync.availableIn.one": "يمكن التحديث بعد دقيقة واحدة.",
  "sync.availableIn.two": "يمكن التحديث بعد دقيقتين.",
  "sync.availableIn.few": "يمكن التحديث بعد {count} دقائق.",
  "sync.availableIn.many": "يمكن التحديث بعد {count} دقيقة.",
  "sync.availableIn.other": "يمكن التحديث بعد {count} دقيقة.",
  "sync.didNotStart": "لم يبدأ التحديث. تحقق من الحالة وحاول مرة أخرى.",
  "sync.availableInMinutes.one": "يمكن التحديث بعد دقيقة واحدة",
  "sync.availableInMinutes.two": "يمكن التحديث بعد دقيقتين",
  "sync.availableInMinutes.few": "يمكن التحديث بعد {count} دقائق",
  "sync.availableInMinutes.many": "يمكن التحديث بعد {count} دقيقة",
  "sync.availableInMinutes.other": "يمكن التحديث بعد {count} دقيقة",
  "sync.refreshAccounts": "تحديث الحسابات",
  "sync.nextRefresh": "التحديث التالي: {time}",
  "sync.refreshNow": "تحديث الحسابات الآن",
  "sync.minutesShort": "{minutes}د",
  "sync.couldNotConfirm": "تعذّر تأكيد التحديث. تحقق من الحالة وأعد المحاولة.",

  "businesses.addBusiness": "إضافة نشاط تجاري",
  "businesses.displayName": "الاسم المعروض",
  "businesses.businessName": "اسم النشاط التجاري",
  "businesses.venues": "الفروع",
  "businesses.enterDisplayName": "أدخل الاسم المعروض",
  "businesses.renamed": "تم تغيير اسم النشاط التجاري بنجاح",
  "businesses.renameFailed": "تعذّر تغيير اسم النشاط التجاري",
  "businesses.add.title": "إضافة نشاط تجاري من Foodics",
  "businesses.add.description":
    "أدخل الرقم المرجعي للنشاط التجاري وبريدك الإلكتروني في ذلك النشاط على Foodics. يتطلب التثبيت الجديد صلاحية إدارة التطبيقات في Foodics، ويتطلب نقل نشاط تجاري مثبّت صلاحية مسؤول في BI ضمن مؤسسته الحالية.",
  "businesses.add.reference": "الرقم المرجعي للنشاط التجاري (رقم F5)",
  "businesses.add.email": "بريدك الإلكتروني في Foodics",
  "businesses.add.code": "الرمز المكوّن من ستة أرقام",
  "businesses.add.requestNewCodeIn": "طلب رمز جديد بعد {time}",
  "businesses.add.requestNewCode": "طلب رمز جديد",
  "businesses.add.tryAgainIn": "حاول مرة أخرى بعد {time}",
  "businesses.add.submit": "إضافة النشاط التجاري",
  "businesses.add.sendCodeIn": "إرسال الرمز بعد {time}",
  "businesses.add.sendCode": "إرسال الرمز",
  "businesses.add.businessNotFound":
    "لم نتمكن من العثور على هذا الرقم المرجعي للنشاط التجاري في Foodics.",
  "businesses.add.userNotFound":
    "لا ينتمي هذا البريد الإلكتروني إلى مستخدم في هذا النشاط التجاري على Foodics.",
  "businesses.add.codeNotSent": "تعذّر إرسال الرمز. يُرجى المحاولة مرة أخرى بعد قليل.",
  "businesses.add.badCode": "تحقق من الرمز وحاول مرة أخرى. إذا انتهت صلاحيته، فاطلب رمزًا جديدًا.",
  "businesses.add.otherAdminMustVerify":
    "يجب أن يتحقق مسؤول في النشاط التجاري الآخر من الرمز لإضافته هنا.",
  "businesses.add.codeUsed":
    "تم استخدام هذا الرمز، أو أن تثبيت النشاط التجاري لا يزال جاريًا. اطلب رمزًا جديدًا إذا لزم الأمر، أو حاول النقل بعد اكتمال التثبيت.",
  "businesses.add.waitForCountdown":
    "يُرجى الانتظار حتى ينتهي العد التنازلي قبل المحاولة مرة أخرى.",
  "businesses.add.unexpectedVerify":
    "منعنا خطأ غير متوقع من إضافة هذا النشاط التجاري أو نقله. يظل الرمز صالحًا حتى انتهاء صلاحيته؛ يُرجى المحاولة مرة أخرى.",
  "businesses.add.unexpectedSend": "منعنا خطأ غير متوقع من إرسال الرمز. يُرجى المحاولة مرة أخرى.",
  "businesses.add.unconfirmedVerify":
    "لم نتمكن من التأكد من إضافة النشاط التجاري أو نقله. حدّث قائمة الأنشطة التجارية قبل المحاولة مرة أخرى.",
  "businesses.add.unconfirmedSend":
    "لم نتمكن من تأكيد طلب الرمز. تحقق من بريدك الإلكتروني قبل المحاولة مرة أخرى.",
  "businesses.add.installing": "تمت إضافة النشاط التجاري. التثبيت قيد التنفيذ.",
  "businesses.add.moved": "تم نقل النشاط التجاري إلى هذه المؤسسة.",
  "businesses.add.alreadyHere": "النشاط التجاري موجود بالفعل في هذه المؤسسة.",

  "budget.title": "الميزانية",
  "budget.year": "السنة",
  "budget.resolution": "الفترة",
  "budget.noBudget": "يبدو أنه لا توجد لديك ميزانية",
  "budget.noBudgetDemo": "لا تتوفر ميزانية في هذا العرض التجريبي",
  "budget.provideXlsx": "ارفع ملف XLSX صالحًا لعرض البيانات",
  "budget.uploadXlsx": "رفع ملف XLSX",
  "budget.uploadTitle": "رفع ملف XLSX",
  "budget.editTitle": "تعديل الميزانية",
  "budget.templates": "القوالب:",
  "budget.uploadedFile": "الملف المرفوع",
  "budget.deleteFile": "حذف الملف",
  "budget.uploadYourFile": "ارفع ملف XLSX الخاص بك",
  "budget.uploadSucceeded": "تم رفع ملف XLSX بنجاح",
  "budget.xlsxOnly": "يُرجى رفع ملف بصيغة XLSX",
  "budget.unexpectedError": "حدث خطأ غير متوقع",
  "budget.invalidFile": "صيغة الملف المرفوع غير صالحة",
  "budget.emptyCell": "توجد خلية فارغة في الصف {row}، العمود {col}",
  "budget.invalidBranchId": 'معرّف الفرع "{branchId}" غير صالح في الصف {row}، العمود {col}',
  "budget.invalidFormat":
    "تنسيق غير صالح في الصف {row}، العمود {col}: القيمة المتوقعة {expected}، والقيمة الفعلية {got}",
  "budget.invalidValue":
    "قيمة غير صالحة في الصف {row}، العمود {col}: القيمة المتوقعة {expected}، والقيمة الفعلية {got}",
  "budget.helpPrefix": "إذا واجهت مشكلة، فاطّلع على التعليمات",
  "budget.helpLink": "هنا",
  "budget.helpSuffix": ".",
  "budget.branch": "الفرع",
  "budget.week": "الأسبوع {week}",
  "budget.error": "خطأ",
  "budget.tryRefreshing": "حاول تحديث الصفحة",
  "budget.pleaseTryRefreshing": "يُرجى محاولة تحديث الصفحة",
} satisfies Messages & ArabicPluralForms;
